import { useMemo, useState } from "react";
import { Col, Row } from "react-grid-system";
import {
  CMInfoUpdateSectionType,
  Option,
  ValidationErrors,
} from "../../../../lib/api/models";
import { convertDateStringToMonthDayYearFormat } from "../../../../lib/formatting";
import { CMInfoFormInput, CMInfoSubtool } from "../../CMInfo.hooks";
import Collapsible from "../../../common/Collapsible";
import LabeledCheckbox from "../LabeledCheckbox";
import styles from "./CMInfoContactPreferenceSubtool.module.css";
import CMInfoSubtoolButtons from "../CMInfoSubtoolButtons";
import ValidationMessage from "../../../common/ValidationMessage/ValidationMessage";
import { partialSuccessErrorMessage } from "../../constants";
import StyledInput from "../../../common/inputs/StyledInput";

const strings = {
  headerText: "Customer Contact Preference",
  instructions:
    "Rank the contact methods from most preferred (1) to least preferred (4)",
  lastVerifiedDate: "Last Verified Date",
  nullDate: "XX/XX/XXXX",
  hideScript: "Hide Script",
  reset: "Reset",
  update: "Update",
};

type ContactPreferenceProps = {
  contactPreference: Record<string, number | null>;
  contactPreferenceOptions: Option[];
  onUpdateContactPreference: (
    contactPreference: Record<string, number | null>,
  ) => void;
  validationMessages: ValidationErrors | undefined;
  isFieldChanged: Record<string, boolean>;
  setIsFieldChanged: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
};

function ContactPreference({
  contactPreference,
  contactPreferenceOptions,
  onUpdateContactPreference,
  validationMessages,
  isFieldChanged,
  setIsFieldChanged,
}: ContactPreferenceProps) {
  const contactPreferenceValueCount = useMemo(
    () =>
      Object.values(contactPreference).reduce<Record<string, number>>(
        (agg, option) =>
          option === null
            ? agg
            : {
                ...agg,
                [option]: (agg[option] ?? 0) + 1,
              },
        {},
      ),
    [contactPreference],
  );

  return contactPreferenceOptions.map((option) => (
    <StyledInput
      key={option.id}
      className={styles.contactPreference}
      label={option.value}
      variant="horizontal"
      type="number"
      max={4}
      min={1}
      invalid={
        validationMessages?.["contactPreference_DUPLICATION"] &&
        contactPreference[option.id] !== null &&
        contactPreferenceValueCount[contactPreference[option.id] ?? 0] > 1
          ? true
          : !isFieldChanged[option.id] &&
            !!validationMessages?.[`contactPreference_${option.id}`]
      }
      value={contactPreference[option.id] ?? ""}
      onChange={(e) => {
        setIsFieldChanged((v) => ({ ...v, [option.id]: true }));
        const value = parseInt(e.currentTarget.value);
        onUpdateContactPreference({
          ...contactPreference,
          [option.id]: isNaN(value) ? null : value,
        });
      }}
    />
  ));
}

type Props = {
  className?: string;
  isScriptOpen: boolean;
  onSetScriptOpen: (open: boolean) => void;
  onShowConfirmationModal: (
    variant: "reset" | "clear",
    onSuccess: () => void,
  ) => void;
  formState: CMInfoFormInput[CMInfoSubtool.ContactPreference];
  initialFormState: CMInfoFormInput[CMInfoSubtool.ContactPreference];
  generalDispatch: (
    payload: Partial<CMInfoFormInput[CMInfoSubtool.ContactPreference]>,
  ) => void;
  contactPreferenceOptions: Option[];
  onUpdate: () => void;
  currentlyUpdating: CMInfoSubtool | "ALL" | null;
  validationMessages: ValidationErrors | undefined;
  failedSections: CMInfoUpdateSectionType[];
  startExpanded: boolean;
};

function hasContactPreferenceSet(
  preferences: Record<string, number | null>,
): boolean {
  for (const key in preferences) {
    if (preferences[key] !== null) {
      return true;
    }
  }
  return false;
}

export default function CMInfoContactPreferenceSubtool({
  className,
  isScriptOpen,
  onSetScriptOpen,
  onShowConfirmationModal,
  formState,
  initialFormState,
  generalDispatch,
  contactPreferenceOptions,
  onUpdate,
  currentlyUpdating,
  validationMessages,
  failedSections,
  startExpanded = false,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(
    startExpanded || !hasContactPreferenceSet(formState.contactPreference),
  );
  const [hasUnverifiedChanges, setHasUnverifiedChanges] = useState(false);

  const [isFieldChanged, setIsFieldChanged] = useState<{
    [key: string]: boolean;
  }>({});

  return (
    <Collapsible
      disabled={formState.isContactPreferenceVerified}
      className={className}
      headerText={strings.headerText}
      isExpanded={isExpanded}
      onClick={() => {
        setIsExpanded(!isExpanded);
        if (isExpanded) {
          onSetScriptOpen(false);
        }
      }}
    >
      <Row className={styles.mb18}>
        <Col>
          <div className={styles.instructions}>{strings.instructions}</div>
        </Col>
      </Row>
      <Row>
        <Col md={5} className={styles.contactPreferenceContainer}>
          <ContactPreference
            contactPreference={formState.contactPreference}
            contactPreferenceOptions={contactPreferenceOptions}
            onUpdateContactPreference={(contactPreference) => {
              generalDispatch({
                contactPreference,
                isContactPreferenceVerified: true,
              });
              setHasUnverifiedChanges(true);
            }}
            validationMessages={validationMessages}
            isFieldChanged={isFieldChanged}
            setIsFieldChanged={setIsFieldChanged}
          />
        </Col>
        <Col offset={{ md: 1 }} md={6} className={styles.rightHandSide}>
          <LabeledCheckbox
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.contactPreferenceLastVerifiedDate,
              strings.nullDate,
            )}
            checked={formState.isContactPreferenceVerified}
            onCheck={(isContactPreferenceVerified) =>
              generalDispatch({ isContactPreferenceVerified })
            }
            isExpired={formState.contactPreferenceDueForVerification}
            disabled={hasUnverifiedChanges}
          />
          {failedSections.includes("contactPreference") ? (
            <ValidationMessage
              validationMessages={[
                partialSuccessErrorMessage(strings.headerText),
              ]}
            />
          ) : null}
          {validationMessages
            ? contactPreferenceOptions.map((option) => (
                <ValidationMessage
                  key={option.id}
                  validationMessages={
                    validationMessages[`contactPreference_${option.id}`] ?? []
                  }
                />
              ))
            : null}
          {validationMessages?.["contactPreference_DUPLICATION"] ? (
            <ValidationMessage
              validationMessages={
                validationMessages["contactPreference_DUPLICATION"]
              }
            />
          ) : null}
          <CMInfoSubtoolButtons
            className={styles.buttonRow}
            hideClearButton
            isScriptOpen={isScriptOpen}
            onSetScriptOpen={onSetScriptOpen}
            onReset={() =>
              onShowConfirmationModal("reset", () => {
                setIsFieldChanged({});
                generalDispatch(initialFormState);
                setHasUnverifiedChanges(false);
              })
            }
            isResetDisabled={!formState.isContactPreferenceVerified}
            onUpdate={() => {
              setIsFieldChanged({});
              onUpdate();
            }}
            isUpdateDisabled={
              !formState.isContactPreferenceVerified || !!currentlyUpdating
            }
            isUpdateLoading={
              currentlyUpdating === CMInfoSubtool.ContactPreference
            }
          />
        </Col>
      </Row>
    </Collapsible>
  );
}
